import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    Route,
    Router,
    RouterStateSnapshot,
    UrlSegment,
    UrlTree
} from '@angular/router';
import { AuthGuard } from '@auth0/auth0-angular';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { ConfigurationService } from '../../system-configuration';
import {
    ApplicationInitializedMessage,
    MessagingService
} from '../../system-services';
import { AuthGuardProxy } from './AuthGuardProxy';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationGuard {
    private hasInitialized = false;
    private authenticationGuard: AuthGuardProxy;
    private currentRetryCount = 0;
    private readonly maxRetryCount: number;
    private readonly delayMs: number;

    constructor(
        private config: ConfigurationService,
        private router: Router,
        private authSdkGuard: AuthGuard,
        private messageService: MessagingService
    ) {
        this.authenticationGuard = this.authSdkGuard;

        this.maxRetryCount =
            this.config.getAuth0Settings().maxRedirectRetryCount;
        this.delayMs = this.config.getAuth0Settings().redirectDelayMs;

        this.messageService
            .listenFor<ApplicationInitializedMessage>(
                ApplicationInitializedMessage
            )
            .subscribe(() => (this.hasInitialized = true));
    }
    canLoad(route: Route, segments: UrlSegment[]): Observable<boolean> {
        return this.authenticationGuard.canLoad(route, segments);
    }

    canActivate(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean> | Observable<UrlTree> {
        return this.canActivateWithRetry(
            state,
            this.authenticationGuard.canActivate(next, state)
        );
    }

    canActivateChild(
        childRoute: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean> | Observable<UrlTree> {
        return this.canActivateWithRetry(
            state,
            this.authenticationGuard.canActivateChild(childRoute, state)
        );
    }

    private canActivateWithRetry(
        state: RouterStateSnapshot,
        source$: Observable<boolean>
    ): Observable<boolean> | Observable<UrlTree> {
        if (!this.hasInitialized) {
            if (this.currentRetryCount >= this.maxRetryCount) {
                return of(false);
            }

            this.currentRetryCount++;

            // reroute back to ourself, the app isn't ready to route here yet.
            const redirectRoute$ = of(this.router.parseUrl(state.url));
            const delayedRedirect$ = redirectRoute$.pipe(delay(this.delayMs));
            return delayedRedirect$;
        }

        this.currentRetryCount = 0;
        return source$;
    }
}
